/**
 * Milestone Detection Utilities
 *
 * Functions for calculating and detecting sync milestones.
 * Milestones: 1, 25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000...
 */

import type { MilestoneConfig } from './types';

/**
 * Standard milestone thresholds
 * After 100, every 100 is a milestone
 */
export const MILESTONE_THRESHOLDS = [1, 25, 50, 100] as const;

/**
 * Check if a count represents a milestone
 * Milestones: 1, 25, 50, 100, 200, 300, 400, 500...
 */
export function isSyncMilestone(count: number): boolean {
  if (count <= 0) return false;
  if (count === 1) return true;
  if (count === 25 || count === 50) return true;
  if (count >= 100 && count % 100 === 0) return true;
  return false;
}

/**
 * Get the current milestone threshold for a given count
 * Returns the highest milestone threshold that the count has reached
 */
export function getMilestoneThreshold(count: number): number {
  if (count <= 0) return 0;
  if (count >= 100) {
    return Math.floor(count / 100) * 100;
  }
  if (count >= 50) return 50;
  if (count >= 25) return 25;
  if (count >= 1) return 1;
  return 0;
}

/**
 * Check if a new milestone was reached by comparing current count to last celebrated
 */
export function checkMilestoneReached(
  currentCount: number,
  lastCelebrated: number,
): { reached: boolean; milestone: number } {
  const currentMilestone = getMilestoneThreshold(currentCount);
  return {
    reached: currentMilestone > lastCelebrated,
    milestone: currentMilestone,
  };
}

/**
 * Get milestone configuration for a given count
 * Returns null if count is not a milestone
 */
export function getMilestoneConfig(count: number): MilestoneConfig | null {
  if (!isSyncMilestone(count)) return null;

  const isSpecial = count === 1 || count === 100 || count === 500 || count % 1000 === 0;

  return {
    count,
    label: getMilestoneLabel(count),
    isSpecial,
  };
}

/**
 * Generate a human-readable milestone label
 */
export function getMilestoneLabel(count: number): string {
  if (count === 1) return 'First sync!';
  if (count === 25) return '25 syncs!';
  if (count === 50) return 'Half century!';
  if (count === 100) return 'Century!';
  if (count === 500) return 'Power user!';
  if (count === 1000) return 'Sync master!';
  if (count % 1000 === 0) return `${count / 1000}K syncs!`;
  return `${count} syncs!`;
}

/**
 * Calculate the next milestone from current count
 */
export function getNextMilestone(currentCount: number): number {
  if (currentCount < 1) return 1;
  if (currentCount < 25) return 25;
  if (currentCount < 50) return 50;
  if (currentCount < 100) return 100;
  // After 100, next milestone is next multiple of 100
  return Math.ceil((currentCount + 1) / 100) * 100;
}

/**
 * Get celebration intensity based on milestone value
 * Used to determine animation intensity
 */
export function getCelebrationIntensity(
  milestoneValue: number,
): 'small' | 'medium' | 'large' | 'epic' {
  if (milestoneValue >= 1000) return 'epic';
  if (milestoneValue >= 100) return 'large';
  if (milestoneValue >= 25) return 'medium';
  return 'small';
}

/**
 * Determine if confetti should show for a milestone
 * Only show for significant milestones (100+)
 */
export function shouldShowConfetti(milestoneValue: number): boolean {
  return milestoneValue >= 100;
}
